<script lang="ts">
  // The time-entry editor: a drawer mounted once in the app shell and driven by
  // `entryEditor` (stores/editor.svelte.ts). Create, edit and duplicate.
  // Form state lives in editor/form.svelte.ts, the sections in editor/*.svelte.
  import '../messages/entries';
  import { untrack } from 'svelte';
  import { Copy, Trash2, Play } from '@lucide/svelte';
  import { Button, Drawer, IconButton } from '../ui';
  import { entryEditor } from '../stores/editor.svelte';
  import { t } from '../i18n.svelte';
  import { confirm } from '../toast.svelte';
  import { navigate } from '../router.svelte';
  import { formatDate } from '../format';
  import { entryDate } from './helpers';
  import { confirmDeleteOne, continueInTimer, deleteOne } from './bulk';
  import { EntryForm } from './editor/form.svelte';
  import TimeSection from './editor/TimeSection.svelte';
  import WhatSection from './editor/WhatSection.svelte';
  import MoneySection from './editor/MoneySection.svelte';
  import PaymentSection from './editor/PaymentSection.svelte';

  const form = new EntryForm();

  // ---------- Init when the drawer opens ----------

  let wasOpen = false;
  /** Re-opened after "keep editing" in the discard dialog: keep the form. */
  let keepForm = false;
  $effect(() => {
    const open = entryEditor.isOpen;
    if (open && !wasOpen) {
      if (keepForm) keepForm = false;
      else untrack(() => form.init());
    }
    wasOpen = open;
  });

  // ---------- Actions ----------

  function close() {
    form.reset();
    entryEditor.close();
  }

  async function save() {
    if (!form.validate()) {
      queueMicrotask(() => document.querySelector<HTMLElement>('.ee [aria-invalid="true"], .ee .invalid input, .ee .invalid')?.focus?.());
      return;
    }
    if (await form.save()) entryEditor.close();
  }

  async function remove() {
    const e = form.editing;
    if (!e || !(await confirmDeleteOne())) return;
    form.deleting = true;
    try {
      if (await deleteOne(e)) close();
    } finally {
      form.deleting = false;
    }
  }

  function duplicate() {
    const e = form.editing;
    if (!e) return;
    entryEditor.open({ duplicate: e });
    form.init();
  }

  async function continueTimer() {
    const e = form.editing;
    if (!e || !(await continueInTimer(e))) return;
    entryEditor.close();
    navigate('/app/timer');
  }

  async function requestClose() {
    if (form.dirty && !form.saving) {
      const ok = await confirm({ title: t('entries.discard_title'), message: t('entries.discard_message'), confirmLabel: t('entries.discard'), danger: true });
      if (!ok) {
        keepForm = true;
        entryEditor.isOpen = true;
        return;
      }
    }
    close();
  }

  function onKey(event: KeyboardEvent) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      (document.activeElement as HTMLElement | null)?.blur?.();
      // Let blur handlers (time / money inputs) commit first.
      setTimeout(save, 0);
    }
  }

  const title = $derived(form.isEdit ? t('entries.edit_title') : entryEditor.duplicateOf ? t('entries.duplicate_title') : t('entries.new_title'));
  const subtitle = $derived(
    form.editing
      ? formatDate(entryDate(form.editing), 'weekday')
      : entryEditor.duplicateOf
        ? t('entries.duplicate_of', { date: formatDate(entryDate(entryEditor.duplicateOf), 'long') })
        : undefined
  );
  const modKey = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';
</script>

<Drawer bind:open={entryEditor.isOpen} {title} {subtitle} width={560} onclose={requestClose}>
  {#snippet headerActions()}
    {#if form.isEdit}
      <IconButton label={t('entries.continue_timer')} onclick={continueTimer}><Play size={17} /></IconButton>
      <IconButton label={t('common.duplicate')} onclick={duplicate}><Copy size={17} /></IconButton>
    {/if}
  {/snippet}

  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <form class="ee" onkeydown={onKey} onsubmit={(e) => e.preventDefault()} novalidate>
    <TimeSection {form} />
    <WhatSection {form} />
    <MoneySection {form} />
    {#if form.isEdit}<PaymentSection {form} />{/if}
  </form>

  {#snippet footer()}
    {#if form.isEdit}
      <Button variant="danger-ghost" loading={form.deleting} onclick={remove}>
        {#snippet icon()}<Trash2 size={16} />{/snippet}
        {t('common.delete')}
      </Button>
    {/if}
    <span class="spacer"></span>
    <Button variant="secondary" onclick={requestClose}>{t('common.cancel')}</Button>
    <Button variant="primary" loading={form.saving} onclick={save} title={`${modKey}+Enter`} kbd={`${modKey}↵`}>
      {form.isEdit ? t('common.save') : t('entries.add')}
    </Button>
  {/snippet}
</Drawer>

<style>
  .ee { display: flex; flex-direction: column; gap: var(--space-6); }
</style>
